import { Pipe, PipeTransform } from '@angular/core';
import { KeywordLocationModel } from '../../models/keyword-location.model';


/**
 * This pipe filters a string by shortening it to begin at a point relative to a given index.
 */
@Pipe({
    name: 'filterStringByIndex',
    standalone: false
})
export class FilterStringByIndexPipe implements PipeTransform {
    /**
     * Gets a substring of the text surrounding a keyword
     *
     * A preamble of a certain word length is defined prior to the given index,
     * and a trailing value of a certain word length is defined for after the given index.
     *
     * If the available space for a trailing value is very short, the preamble will be extended.
     *
     * The preamble will otherwise be defined by a cushion of word count, or by the start of the sentence within
     * the wordcount cushion.
     *
     * @param text content to be filtered
     * @param keywordIndices start and end index of a keyword in the string
     * @param inFrontOfStartIndexWordCushion number of words to include before the index (unless a sentence begins)
     * @param maxWordCount the max number of words in the resulting string
     *
     * @returns string - filtered string
     */
    transform(text: string, keywordIndices: KeywordLocationModel, inFrontOfStartIndexWordCushion: number, maxWordCount: number): string {
        if (!keywordIndices) {
            // if no keyword index return text with maxWordCount applied

            return this.truncateWords(text, maxWordCount);
        }

        const keywordString = text.substring(keywordIndices.characterStartPoint, keywordIndices.characterEndPoint);
        const numberOfWordsInKeywordPhrase = keywordString.split(/\s+/).length;

        if (numberOfWordsInKeywordPhrase >= maxWordCount) {
            // there are more words in keyword phrase than the limit

            return keywordString;
        }

        let preamble = this.getTextBeforeKeywordStartIndex(text, keywordIndices, inFrontOfStartIndexWordCushion);
        const numberOfWordsInPreamble = preamble.split(/\s+/).length;

        const excessWords = (numberOfWordsInPreamble + numberOfWordsInKeywordPhrase) - maxWordCount;
        if (excessWords > 0) {
            // there are too many words in the preamble + keyword

            return this.truncateWords(preamble + keywordString, -excessWords);
        }

        const textAfterKeywordEndPoint = text.substring(keywordIndices.characterEndPoint);

        const staging = preamble + keywordString + textAfterKeywordEndPoint;
        const numberOfWordsInStaging = staging.split(/\s+/).length;

        if (numberOfWordsInStaging < maxWordCount) {
            // not enough words after keyword, add more to the front

            const numberOfWordsNeeded = maxWordCount - numberOfWordsInStaging;
            preamble = this.truncateWords(
                text.substring(0, keywordIndices.characterStartPoint),
                -numberOfWordsNeeded
            );
            const finalString = preamble + keywordString + textAfterKeywordEndPoint;

            return finalString;
        }

        return this.truncateWords(staging, maxWordCount);
    }

    /**
     * Truncates a string based on a word limit
     * (stolen from ng-truncate)
     *
     * @param value input value
     * @param limit number of words to limit by
     * @param trail symbol to indicate that the string was truncated
     *
     * @returns string
     */
    truncateWords(value: string, limit: number = 40, trail: string = '…'): string {
        let result = value || '';
        if (value) {
            const words = value.split(/\s+/);
            if (words.length > Math.abs(limit)) {
                if (limit < 0) {
                    limit *= -1;
                    result = trail + words.slice(words.length - limit, words.length).join(' ');
                } else {
                    result = words.slice(0, limit).join(' ') + trail;
                }
            }
        }

        return result;
    }

    /**
     * Gets text to be included in the result that will appear before the keyword start index
     *
     * @param text characters to be filtered
     * @param keywordIndicies index to mark the end of the return string
     * @param inFrontOfStartIndexWordCushion
     *
     * @returns string
     */
    getTextBeforeKeywordStartIndex(
        text: string,
        keywordIndices: KeywordLocationModel,
        inFrontOfStartIndexWordCushion: number
    ): string {
        let textBeforeStartIndex = this.truncateWords(
            text.substring(0, keywordIndices.characterStartPoint),
            -inFrontOfStartIndexWordCushion
        );
        const startOfSentenceMarker = textBeforeStartIndex.lastIndexOf('.');
        if (startOfSentenceMarker > 0) {
            textBeforeStartIndex = '…' + textBeforeStartIndex.substring(startOfSentenceMarker + 1);
        }

        return textBeforeStartIndex;
    }

}
